import { FC } from 'react';
import { faMessageLines } from '@fortawesome/pro-regular-svg-icons';
import { Flex } from 'portal-commons';

import CampaignAsset from './CampaignAsset';
import RcsSectionBlock from './RcsSectionBlock';
import InfoItemLayout from './InfoItemLayout';
import InfoItem from './InfoItem';
import StatusIndicator from './StatusIndicator';
import { CampaignDetail } from '@/apis/core/types';
import { humanize } from '@/utils/string';

interface CampaignDetailsSectionProps {
  campaign?: CampaignDetail;
  banner?: Record<string, any>;
  logo?: Record<string, any>;
  usecaseMap: Record<string, string>;
}

const CampaignDetailsSection: FC<CampaignDetailsSectionProps> = ({
  campaign,
  banner,
  logo,
  usecaseMap,
}) => {
  const handleUserDownload = (url: string, fileName: string) => {
    const link: HTMLAnchorElement = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();

    // Clean up and remove the link
    link.parentNode?.removeChild(link);
  };

  return (
    <RcsSectionBlock
      icon={faMessageLines}
      title="Campaign Details"
      showDivider={true}
    >
      <InfoItemLayout.Vertical>
        <InfoItemLayout.Horizontal>
          <InfoItem>
            {logo?.name ? (
              <CampaignAsset
                type="logo"
                name={logo.name}
                url={logo.url}
                status={logo.verificationStatus?.toLowerCase() || ''}
                dimension={{ width: '127px', height: '127px' }}
                onDownload={() => handleUserDownload(logo.url, logo.name)}
              />
            ) : (
              <div />
            )}
          </InfoItem>
          <InfoItem>
            {banner?.name ? (
              <CampaignAsset
                type="banner"
                name={banner.name}
                url={banner.url}
                status={banner.verificationStatus?.toLowerCase() || ''}
                dimension={{ width: '379px', height: '127px' }}
                onDownload={() => handleUserDownload(banner.url, banner.name)}
              />
            ) : (
              <div />
            )}
          </InfoItem>
        </InfoItemLayout.Horizontal>

        <InfoItemLayout.Horizontal>
          <InfoItemLayout.Vertical>
            <InfoItem title="ID">{campaign?.uid}</InfoItem>
            <InfoItem title="Display Name">{campaign?.displayName}</InfoItem>
            <InfoItem title="Background Color">
              <Flex sx={{ alignItems: 'center' }}>
                {campaign?.color && (
                  <StatusIndicator
                    shape="square"
                    color={campaign?.color ? `#${campaign?.color}` : undefined}
                    size="16px"
                  />
                )}
                #{campaign?.color}
              </Flex>
            </InfoItem>
            <InfoItem title="Hosting Region">
              {humanize(campaign?.hostingRegion)}
            </InfoItem>
            <InfoItem title="Billing Category">
              {humanize(campaign?.billingCategory)}
            </InfoItem>
          </InfoItemLayout.Vertical>
          <InfoItemLayout.Vertical>
            <InfoItem
              title="Short Description"
              sx={{
                flexGrow: 0,
                '& > div': {
                  maxWidth: 'calc(60% - 12px)',
                },
              }}
            >
              {campaign?.shortDescription}
            </InfoItem>
            <InfoItem title="Use Case" sx={{ flexGrow: 0 }}>
              {campaign?.usecase &&
                (usecaseMap[campaign?.usecase] || campaign?.usecase)}
            </InfoItem>
            <InfoItem title="Terms of Use URL" sx={{ flexGrow: 0 }}>
              {campaign?.termsAndConditionsLink}
            </InfoItem>
            <InfoItem title="Privacy Policy URL" sx={{ flexGrow: 0 }}>
              {campaign?.privacyPolicyLink}
            </InfoItem>
          </InfoItemLayout.Vertical>
        </InfoItemLayout.Horizontal>

        <InfoItemLayout.Horizontal sx={{ height: '18px' }} />
      </InfoItemLayout.Vertical>

      <InfoItemLayout.Vertical sx={{ gap: 'xs' }}>
        {campaign?.websites?.map((website, index) => (
          <InfoItemLayout.Horizontal key={`website-${index}`}>
            <InfoItem title={`${index === 0 ? 'Primary ' : ''}Website`}>
              {website}
            </InfoItem>
            <InfoItem
              title={`Label for ${index === 0 ? 'Primary ' : ''}Website`}
            >
              {campaign?.websiteLabels?.[index]}
            </InfoItem>
          </InfoItemLayout.Horizontal>
        ))}

        {campaign?.phones?.map((phone, index) => (
          <InfoItemLayout.Horizontal
            key={`phone-${index}`}
            sx={{ mt: `${index === 0 ? '12px' : '0'}` }}
          >
            <InfoItem title={`${index === 0 ? 'Primary ' : ''}Phone Number`}>
              {phone}
            </InfoItem>
            <InfoItem
              title={`Label for ${index === 0 ? 'Primary ' : ''}Phone Number`}
            >
              {campaign?.phoneLabels?.[index]}
            </InfoItem>
          </InfoItemLayout.Horizontal>
        ))}

        {campaign?.emails?.map((email, index) => (
          <InfoItemLayout.Horizontal
            key={`email-${index}`}
            sx={{ mt: `${index === 0 ? '12px' : '0'}` }}
          >
            <InfoItem title={`${index === 0 ? 'Primary ' : ''}Email ID`}>
              {email}
            </InfoItem>
            <InfoItem
              title={`Label for ${index === 0 ? 'Primary ' : ''}Email ID`}
            >
              {campaign?.emailLabels?.[index]}
            </InfoItem>
          </InfoItemLayout.Horizontal>
        ))}
      </InfoItemLayout.Vertical>
    </RcsSectionBlock>
  );
};

export default CampaignDetailsSection;
